import React, { useCallback, useMemo } from 'react';
import { Button, Table } from 'react-bootstrap';
import Select from 'react-select';

import { MenuMaster, RoleMaster, RoleMasterForm } from 'models/role';
import { twMerge } from 'tailwind-merge';
import { useMasterMenu } from 'presentations/views/WorkflowMenuMasterConfig/queries';
import { FaEdit, FaTimesCircle } from 'react-icons/fa';
import { useMasterCompany } from '../../../../queries';

export type WorkflowRoleMasterTableProps = {
    roleMasterForms?: RoleMasterForm[];
    isPending?: boolean;
    className?: string;
    onEdit?: (roleMasterForm: RoleMasterForm) => void;
    onDelete?: (roleMasterForm: RoleMasterForm) => void;
};

export const WorkflowRoleMasterTable = (
    props: WorkflowRoleMasterTableProps
) => {
    const { isPending, roleMasterForms, className, onEdit, onDelete } = props;

    const { data: masterCompanies } = useMasterCompany({ status: 'active' });
    const masterCompanyMap = useMemo(
        () => new Map(masterCompanies?.map(company => [company.sid, company])),
        [masterCompanies]
    );

    const { data: masterMenu = [] } = useMasterMenu();

    const getMasterMenuLabel = useCallback(
        //(masterMenu: MenuMaster) => `${masterMenu.id}: ${masterMenu.menu_name}`,
        (masterMenu: MenuMaster) => `${masterMenu.menu_name}`,
        []
    );

    const getMasterMenuValue = useCallback(
        (masterMenu: MenuMaster) => masterMenu.id,
        []
    );

    return (
        <div className={twMerge('flex-1', className)}>
            <Table
                size="sm"
                className="table-bordered table-auto align-middle"
            >
                <thead>
                    <tr>
                        <th />
                        <th>Company Name</th>
                        <th>Role ID</th>
                        <th>Permissions</th>
                    </tr>
                </thead>
                <tbody>
                    {isPending ? (
                        <tr>
                            <td colSpan={99}>
                                <div className="flex items-center justify-center">
                                    <div className="spinner-border text-primary my-2" />
                                </div>
                            </td>
                        </tr>
                    ) : (
                        roleMasterForms?.map((roleMasterForm, index) => (
                            <tr key={index}>
                                <td className="whitespace-nowrap align-middle">
                                    <Button
                                        size="sm"
                                        variant="outline-info"
                                        onClick={() => onEdit?.(roleMasterForm)}
                                    >
                                        <FaEdit />
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline-danger"
                                        className="ml-1"
                                        onClick={() =>
                                            onDelete?.(roleMasterForm)
                                        }
                                    >
                                        <FaTimesCircle />
                                    </Button>
                                </td>
                                <td className="text-center align-middle">
                                    {roleMasterForm.sid === '*'
                                        ? roleMasterForm.sid
                                        : masterCompanyMap.get(
                                              roleMasterForm.sid
                                          )?.company_name}
                                </td>
                                <td className="align-middle">
                                    {roleMasterForm.id}
                                </td>
                                <td className="align-middle">
                                    <Select
                                        isMulti
                                        isDisabled
                                        options={masterMenu}
                                        value={roleMasterForm.allowMenus
                                            .slice(0)
                                            .sort((a, b) =>
                                                a.menu_name.localeCompare(
                                                    b.menu_name
                                                )
                                            )}
                                        getOptionLabel={getMasterMenuLabel}
                                        getOptionValue={getMasterMenuValue}
                                    />
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
        </div>
    );
};
